import React from "react";
import ProductPageLayout from "@/components/ProductPageLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, Mail, AlertTriangle, Clock, Users, Gavel } from "lucide-react";

const VulnerabilityDisclosure = () => {
  return (
    <ProductPageLayout
      title="Responsible Disclosure Policy"
      description="Assembly's responsible disclosure policy for reporting security vulnerabilities and issues. Learn how to securely report security concerns."
      keywords="vulnerability disclosure, security policy, responsible disclosure, security reporting, bug bounty"
      ctaTitle="Questions About Security?"
      ctaDescription="Contact our security team if you have questions about our disclosure policy or security practices."
      ctaButtonText="Contact Security"
      ctaVariant="blue"
      showCTA={false}
    >
      {/* Hero Section */}
      <section className="pt-16 sm:pt-20 lg:pt-24 pb-12 sm:pb-16 lg:pb-20 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center">
            <div className="flex justify-center items-center gap-3 mb-4 sm:mb-6">
              <Shield className="h-8 w-8 text-red-600" />
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
                Responsible Disclosure Policy
              </h1>
            </div>
            <p className="text-lg text-gray-600 mb-6">
              Last updated on April 29, 2022
            </p>
            <div className="flex items-center justify-center gap-2">
              <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 border-blue-200 text-base font-medium px-4 py-2">
                Assembly's SOC 2 Type II compliance is monitored by Vanta
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          
          {/* Reporting Security Issues */}
          <Card className="mb-12 border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="h-6 w-6 text-red-600" />
                <h2 className="text-2xl font-bold text-gray-900">Reporting Security Issues</h2>
              </div>
              <p className="text-gray-700 mb-4">
                If you believe you've found something in Assembly that has security implications, 
                please email them to{" "}
                <a 
                  href="mailto:support@joinassembly.com" 
                  className="text-blue-600 hover:text-blue-800 font-medium underline"
                >
                  support@joinassembly.com
                </a>
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">
                  * SSO is included with some plans or can be purchased as an add-on for others.
                </p>
                <p className="text-sm text-gray-600">
                  ** Security events are logged for all accounts, but these are only accessible on some plans.
                </p>
              </div>
              <p className="text-gray-700 mt-4">
                Data security is a top priority for Assembly, and Assembly believes that working with 
                skilled security researchers can identify weaknesses in any technology. If you believe 
                you've found a security vulnerability in Assembly's service, please notify us; we will 
                work with you to resolve the issue promptly.
              </p>
            </CardContent>
          </Card>

          {/* Disclosure Policy */}
          <Card className="mb-12 border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="h-6 w-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">Disclosure Policy</h2>
              </div>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    If you believe you've discovered a potential vulnerability, please let us know by 
                    emailing us at{" "}
                    <a 
                      href="mailto:security@joinassembly.com" 
                      className="text-blue-600 hover:text-blue-800 font-medium underline"
                    >
                      security@joinassembly.com
                    </a>
                    . We will acknowledge your email within five business days.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    Provide us with a reasonable amount of time to resolve the issue before 
                    disclosing it to the public or a third party. We aim to resolve critical issues 
                    within ten business days of disclosure.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    Make a good faith effort to avoid violating privacy, destroying data, or 
                    interrupting or degrading the Assembly service. Please only interact with 
                    accounts you own or for which you have explicit permission from the account holder.
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Exclusions */}
          <Card className="mb-12 border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="h-6 w-6 text-orange-600" />
                <h2 className="text-2xl font-bold text-gray-900">Exclusions</h2>
              </div>
              <p className="text-gray-700 mb-4">While researching, we'd like you to refrain from:</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
                  Distributed Denial of Service (DDoS)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
                  Spamming
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
                  Social engineering or phishing of Assembly employees or contractors
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
                  Any attacks against Assembly's physical property or data centers
                </li>
              </ul>
              <p className="text-gray-700 mt-4 font-medium">
                Thank you for helping to keep Assembly and our users safe!
              </p>
            </CardContent>
          </Card>

          {/* Changes */}
          <Card className="mb-12 border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="h-6 w-6 text-green-600" />
                <h2 className="text-2xl font-bold text-gray-900">Changes</h2>
              </div>
              <p className="text-gray-700">
                We may revise these guidelines from time to time. The most current version of the 
                guidelines will be available at{" "}
                <a 
                  href="https://joinassembly.com/vulnerability-disclosure-policy" 
                  className="text-blue-600 hover:text-blue-800 font-medium underline"
                >
                  joinassembly.com/vulnerability-disclosure-policy
                </a>
                .
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="mb-12 border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <Mail className="h-6 w-6 text-purple-600" />
                <h2 className="text-2xl font-bold text-gray-900">Contact</h2>
              </div>
              <p className="text-gray-700">
                Assembly is always open to feedback, questions, and suggestions. If you would like 
                to talk to us, please feel free to email us at{" "}
                <a 
                  href="mailto:feedback@joinassembly.com" 
                  className="text-blue-600 hover:text-blue-800 font-medium underline"
                >
                  feedback@joinassembly.com
                </a>
                .
              </p>
            </CardContent>
          </Card>

          {/* Disciplinary Action */}
          <Card className="mb-12 border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <Gavel className="h-6 w-6 text-red-600" />
                <h2 className="text-2xl font-bold text-gray-900">Disciplinary Action</h2>
              </div>
              <p className="text-gray-700">
                Employees who violate this policy may face disciplinary consequences in proportion 
                to their violation. Assembly management will determine how serious an employee's 
                offense is and take the appropriate action.
              </p>
            </CardContent>
          </Card>

          {/* Responsibility */}
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <Users className="h-6 w-6 text-indigo-600" />
                <h2 className="text-2xl font-bold text-gray-900">Responsibility</h2>
              </div>
              <p className="text-gray-700">
                It is the CTO's responsibility to see this policy is enforced.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </ProductPageLayout>
  );
};

export default VulnerabilityDisclosure;